/**
 * Beheer › Menu & prijzen, as in the prototype: categories, products and shared choice groups. These
 * functions only build the next menu; the server checks it again and stores it as a new version.
 * Fields the till does not know (sources of the import, the website's settings) are always kept.
 */
import type { Category, Channel, Menu, ModifierGroup, ModifierItem, Product, VatRate } from "../../api/types";
import { formatAmount, eurosToCents } from "../../lib/money";
import { moved } from "../../lib/reorder";
import { categoryColor, productsIn } from "../sale/menu-view";

/**
 * The tile colours to choose from, in two groups (Furkan, 03/10/2026: "een optie neon, andere optie doffe
 * kleuren gelijk nu ... en graag veel meer kleuren"); the prototype had ten.
 *
 * "Neon": 24 hues round the colour circle, 15 degrees apart, each as strong as a screen can show it and
 * light enough to stand out on a black tile as on a white one.
 *
 * "Dof": the quiet colours a tile has today, which are the ten of the prototype and those of a menu that
 * names none (menu-view.ts), so the colour a category shows is always one to choose; six more fill the
 * gaps between their hues.
 *
 * Both run through the hues from red to pink; the two greys close "Dof".
 */
export const COLOR_GROUPS = [
  {
    key: "neon",
    colors: [
      "#FF3D54",
      "#FF5F37",
      "#FF822B",
      "#FF9F2A",
      "#FFBA27",
      "#FDD500",
      "#E7E800",
      "#B6EE00",
      "#4EF500",
      "#00EC89",
      "#00E5B4",
      "#00E2D0",
      "#00D9E2",
      "#00C4E4",
      "#00AFE7",
      "#0098EE",
      "#2E7DFF",
      "#5A60FF",
      "#8352FF",
      "#AA3FFF",
      "#DA00FF",
      "#F300D7",
      "#FF04A9",
      "#FF2F7E",
    ],
  },
  {
    key: "dull",
    colors: [
      "#A33B5A",
      "#B03A5B",
      "#B94642",
      "#9A4B2B",
      "#C8541C",
      "#C8741C",
      "#8A6D3B",
      "#B0892A",
      "#C9A227",
      "#969031",
      "#6E8B3D",
      "#4E8F3A",
      "#2F7D3B",
      "#37825E",
      "#0E6A5A",
      "#2A7F86",
      "#3E7C9A",
      "#2F6FA3",
      "#5B6FA8",
      "#6355A2",
      "#7B4B94",
      "#A15186",
      "#8A7F72",
      "#4F6D7A",
    ],
  },
] as const;

/** A new category gets the next of these: the ten of the prototype, in its order. */
const NEW_CATEGORY_COLORS = [
  "#C8541C",
  "#B0892A",
  "#0E6A5A",
  "#3E7C9A",
  "#7B4B94",
  "#8A6D3B",
  "#C9A227",
  "#4F6D7A",
  "#A33B5A",
  "#2F7D3B",
] as const;

const DEFAULT_VARIANT = "Standaard";
/** "13,90", "0,50": as prices are written in the editor. */
const decimal = (euros: number) => euros.toFixed(2).replace(".", ",");
/** The highest price the server takes (menu.model.ts). */
const MAX_PRICE = 100_000;
export const DEFAULT_SIZES = ["Small", "Medium", "Large"];

/** How a size is named before its price in the list: "S" for Small, "G" for Groot, "33 cl" as it is. */
export function sizeShort(label: string): string {
  return /^(small|medium|large|solo|maxi|mini|klein|groot)$/i.test(label) ? label[0]! : label;
}

/** "S 11,90 · M 13,90 · L 16,90", "33 cl 2,75 · 1,5 l 5,00" or "2,75", as in the prototype. */
export function priceText(product: Product): string {
  if (product.variants.length === 1) return formatAmount(eurosToCents(product.variants[0]!.price));
  return product.variants
    .map((variant) => `${sizeShort(variant.label)} ${formatAmount(eurosToCents(variant.price))}`)
    .join(" · ");
}

/** A price in the list of Menu & prijzen: one size of one product. */
export const priceKey = (productId: string, index: number): string => `${productId}#${String(index)}`;

/**
 * A price typed in the list of Menu & prijzen (Furkan, 09/10/2026): "14", "14,5", "14,50" or "14.50",
 * with or without "€"; NaN when it is not a price a product can have (more than zero, at most two
 * decimals, as the product editor and the server ask).
 */
export function typedPrice(text: string): number {
  const clean = text.replace(/€/g, "").replace(/\s/g, "");
  if (!/^\d{1,6}([.,]\d{1,2})?$/.test(clean)) return Number.NaN;
  const value = parseEuros(clean);
  return value > 0 && value <= MAX_PRICE ? value : Number.NaN;
}

/**
 * The menu with the prices typed in the list (by `priceKey`), and what changed, for the logbook of the
 * shop: "Margherita Medium 13,90 → 14,50". A price of a product or a size that is gone meanwhile (another
 * device) is left out; a price that stays the same changes nothing.
 */
export function withPrices(menu: Menu, prices: ReadonlyMap<string, number>): { menu: Menu; changed: string[] } {
  const products = { ...menu.products };
  const changed: string[] = [];
  for (const [key, price] of prices) {
    const at = key.lastIndexOf("#");
    const productId = key.slice(0, at);
    const index = Number(key.slice(at + 1));
    const product = Object.hasOwn(products, productId) ? products[productId]! : undefined;
    const variant = product?.variants[index];
    if (!product || !variant || variant.price === price) continue;
    products[productId] = {
      ...product,
      variants: product.variants.map((item, place) => (place === index ? { ...item, price } : item)),
    };
    const size = product.variants.length > 1 ? ` ${variant.label}` : "";
    changed.push(`${product.name}${size} ${decimal(variant.price)} → ${decimal(price)}`);
  }
  return { menu: changed.length === 0 ? menu : { ...menu, products }, changed };
}

/** A readable, unique id for something new: "prod_durum_kipfilet", "cat_wraps", "mg_sauzen". */
export function newId(prefix: string, name: string, taken: (id: string) => boolean): string {
  const slug =
    name
      .normalize("NFD")
      .replace(/[̀-ͯ]/g, "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "")
      .slice(0, 40) || "nieuw";
  let id = `${prefix}${slug}`;
  for (let counter = 2; taken(id); counter++) id = `${prefix}${slug}_${String(counter)}`;
  return id;
}

// categories

export interface CategoryDraft {
  name: string;
  color: string;
  webSplitBySize: boolean;
  tillSplitBySize: boolean;
  vatTakeaway: VatRate;
  vatOnSite: VatRate;
}

export function categoryDraft(menu: Menu, category: Category | null): CategoryDraft {
  return {
    name: category?.name ?? "",
    // an existing category keeps the colour it shows now; a new one gets the next colour
    color: category
      ? categoryColor(category, menu.categories.indexOf(category))
      : NEW_CATEGORY_COLORS[menu.categories.length % NEW_CATEGORY_COLORS.length]!,
    webSplitBySize: category?.webSplitBySize === true,
    tillSplitBySize: category?.tillSplitBySize === true,
    vatTakeaway: category?.vat?.takeaway ?? 6,
    vatOnSite: category?.vat?.onSite ?? 12,
  };
}

export function saveCategory(menu: Menu, category: Category | null, draft: CategoryDraft): Menu {
  const fields = {
    name: draft.name.trim(),
    color: draft.color,
    webSplitBySize: draft.webSplitBySize,
    tillSplitBySize: draft.tillSplitBySize,
    vat: { takeaway: draft.vatTakeaway, onSite: draft.vatOnSite },
  };
  if (category) {
    return {
      ...menu,
      categories: menu.categories.map((candidate) =>
        candidate.id === category.id ? { ...candidate, ...fields } : candidate,
      ),
    };
  }
  const id = newId("cat_", fields.name, (taken) => menu.categories.some((candidate) => candidate.id === taken));
  return { ...menu, categories: [...menu.categories, { id, ...fields }] };
}

/** Only an empty category can be deleted (prototype). */
export function deleteCategory(menu: Menu, categoryId: string): Menu {
  if (Object.values(menu.products).some((product) => product.categoryId === categoryId)) return menu;
  return { ...menu, categories: menu.categories.filter((category) => category.id !== categoryId) };
}

export function moveCategory(menu: Menu, categoryId: string, step: -1 | 1): Menu {
  const from = menu.categories.findIndex((category) => category.id === categoryId);
  const to = from + step;
  if (from < 0 || to < 0 || to >= menu.categories.length) return menu;
  const categories = menu.categories.slice();
  [categories[from], categories[to]] = [categories[to]!, categories[from]!];
  return { ...menu, categories };
}

// products

export interface ProductDraft {
  name: string;
  receiptName: string;
  code: string;
  categoryId: string;
  description: string;
  /** Formaten (true) or one price (false). */
  sizes: boolean;
  variants: { label: string; price: string }[];
  price: string;
  ingredients: string[];
  /** Linked groups. */
  groups: string[];
  /** Free picks per linked group, as typed. */
  free: Record<string, string>;
  alcohol: boolean;
  special: boolean;
  soldOut: boolean;
  /** The allergen toggles; null = Tafo's proposal, untouched. */
  allergens: string[] | null;
}

export const parseEuros = (text: string): number => {
  const value = Number.parseFloat(text.trim().replace(",", "."));
  return Number.isFinite(value) ? Math.round(value * 100) / 100 : Number.NaN;
};

/**
 * The product editor follows a price changed in the list meanwhile (Furkan, 09/10/2026: prices straight
 * in the list): a price field that still shows the old price takes the new one, so saving the editor
 * afterwards never puts the old price back. A price typed in the editor itself stays as it is typed.
 */
export function followPrices(draft: ProductDraft, before: readonly number[], now: readonly number[]): ProductDraft {
  if (before.length !== now.length) return draft;
  const follow = (typed: string, index: number) =>
    parseEuros(typed) === before[index] && before[index] !== now[index] ? decimal(now[index]!) : typed;
  if (!draft.sizes) {
    const price = now.length === 1 ? follow(draft.price, 0) : draft.price;
    return price === draft.price ? draft : { ...draft, price };
  }
  if (draft.variants.length !== now.length) return draft;
  const variants = draft.variants.map((variant, index) => ({ ...variant, price: follow(variant.price, index) }));
  return variants.every((variant, index) => variant.price === draft.variants[index]!.price)
    ? draft
    : { ...draft, variants };
}

export function productDraft(product: Product | null, categoryId: string, soldOut: boolean): ProductDraft {
  const sizes = (product?.variants.length ?? 0) > 1;
  return {
    name: product?.name ?? "",
    receiptName: product?.receiptName ?? "",
    code: product?.code ?? "",
    categoryId: product?.categoryId ?? categoryId,
    description: product?.description ?? "",
    sizes,
    variants: sizes
      ? product!.variants.map((variant) => ({ label: variant.label, price: decimal(variant.price) }))
      : DEFAULT_SIZES.map((label) => ({ label, price: "0" })),
    price: product && !sizes ? decimal(product.variants[0]?.price ?? 0) : "0",
    ingredients: product?.ingredients?.slice() ?? [],
    groups: product?.modifierGroupIds?.slice() ?? [],
    free: Object.fromEntries(
      Object.entries(product?.freeQuantities ?? {}).map(([group, count]) => [group, String(count)]),
    ),
    alcohol: product?.alcohol === true,
    special: product?.special === true,
    soldOut,
    allergens: null,
  };
}

/** The message of the prototype when something is missing, or null when the draft can be saved. */
export function checkProduct(menu: Menu, draft: ProductDraft, original: Product | null): string | null {
  if (draft.name.trim() === "") return "Geef het product een naam";
  const code = normalizeCode(draft.code);
  if (code === "") return "Geef het product een code";
  const taken = Object.values(menu.products).some((product) => product.code === code && product.id !== original?.id);
  if (taken) return `Code ${code} bestaat al`;
  const prices = draft.sizes ? draft.variants.map((variant) => parseEuros(variant.price)) : [parseEuros(draft.price)];
  if (prices.some((price) => !(price > 0))) return "Vul een prijs in groter dan 0";
  if (draft.sizes && draft.variants.some((variant) => variant.label.trim() === "")) return "Geef elk formaat een naam";
  return null;
}

export const normalizeCode = (code: string): string => code.toUpperCase().replace(/[^A-Z0-9]/g, "");

/** What the product window asks first: what must be chosen, then what may be added, how it is made last. */
function askedAt(group: ModifierGroup): number {
  if (group.kind === "INSTRUCTION") return 2;
  return group.kind === "CHOICE" && group.minimum > 0 ? 0 : 1;
}

/**
 * The linked groups in the order the product window asks them. The groups the product already had keep
 * their order: changing a price never moves "Kies je pasta" below the extras. A group that is linked now
 * takes its place among them: a required choice at the top, extras after it, instructions last.
 *
 * The order of the groups in the menu itself only settles which of two new ones comes first. More does
 * not rest on it: a group made later stands last in the menu whatever it asks, and a menu stored before
 * the server kept the order of the card (migration 0036) has its groups by id.
 */
function linkedGroups(menu: Menu, original: Product | null, draft: Pick<ProductDraft, "groups">): string[] {
  const linked = (groupId: string) => draft.groups.includes(groupId) && Object.hasOwn(menu.modifierGroups, groupId);
  const order = [...new Set(original?.modifierGroupIds ?? [])].filter(linked);
  for (const groupId of Object.keys(menu.modifierGroups)) {
    if (!linked(groupId) || order.includes(groupId)) continue;
    const mine = askedAt(menu.modifierGroups[groupId]!);
    // right after the last group that is asked before it or together with it
    let at = 0;
    order.forEach((other, index) => {
      if (askedAt(menu.modifierGroups[other]!) <= mine) at = index + 1;
    });
    order.splice(at, 0, groupId);
  }
  return order;
}

/**
 * The product as the draft describes it. A single price keeps the label of the one size it had
 * ("33 cl" stays on the receipt); switching to sizes starts from Small / Medium / Large.
 */
export function productFromDraft(menu: Menu, original: Product | null, draft: ProductDraft, id: string): Product {
  const singleLabel = original?.variants.length === 1 ? original.variants[0]!.label : DEFAULT_VARIANT;
  const variants = draft.sizes
    ? draft.variants.map((variant, index) => ({
        ...(original?.variants[index] ?? {}),
        label: variant.label.trim(),
        price: parseEuros(variant.price),
      }))
    : [
        {
          ...(original?.variants.length === 1 ? original.variants[0] : {}),
          label: singleLabel,
          price: parseEuros(draft.price),
        },
      ];
  const groups = linkedGroups(menu, original, draft);
  const freeQuantities: Record<string, number> = {};
  for (const groupId of groups) {
    const typed = draft.free[groupId];
    if (typed === undefined || typed.trim() === "") continue;
    const count = Math.max(0, Math.min(20, Number.parseInt(typed, 10) || 0));
    if (count !== menu.modifierGroups[groupId]!.freeQuantity) freeQuantities[groupId] = count;
  }
  const name = draft.name.trim();
  const receiptName = draft.receiptName.trim();
  const description = draft.description.trim();
  const {
    receiptName: _receiptName,
    freeQuantities: _free,
    special: _special,
    alcohol: _alcohol,
    ...kept
  } = original ?? ({} as Partial<Product>);
  return {
    ...kept,
    id,
    name,
    code: normalizeCode(draft.code),
    categoryId: draft.categoryId,
    description,
    ingredients: draft.ingredients.slice(),
    variants,
    modifierGroupIds: groups,
    ...(receiptName !== "" && receiptName !== name ? { receiptName } : {}),
    ...(Object.keys(freeQuantities).length > 0 ? { freeQuantities } : {}),
    ...(draft.alcohol ? { alcohol: true } : {}),
    ...(draft.special ? { special: true } : {}),
  };
}

/** Saves a product (new or existing); returns the next menu and the product's id. */
export function saveProduct(
  menu: Menu,
  original: Product | null,
  draft: ProductDraft,
  allergens: (menu: Menu, product: Product, codes: readonly string[]) => Product,
): { menu: Menu; id: string } {
  const id = original?.id ?? newId("prod_", draft.name, (taken) => Object.hasOwn(menu.products, taken));
  let product = productFromDraft(menu, original, draft, id);
  if (draft.allergens !== null) product = allergens(menu, product, draft.allergens);
  return {
    menu: {
      ...menu,
      // in another category now: it leaves the own order of the one it was in, and comes last in the new one
      categories: outOfOrders(menu.categories, id, product.categoryId),
      products: { ...menu.products, [id]: product },
    },
    id,
  };
}

/** A product is in the own order of its category only: out of that of every other one. */
function outOfOrders(categories: Category[], productId: string, categoryId: string | null): Category[] {
  return categories.map((category) =>
    category.id !== categoryId && category.productOrder?.includes(productId) === true
      ? { ...category, productOrder: category.productOrder.filter((id) => id !== productId) }
      : category,
  );
}

/**
 * Removes a product from the menu, from every choice list that offers it (a pizza is also a half of a
 * Splitza) and from the own order of its category. Old orders keep their own copy.
 */
export function deleteProduct(menu: Menu, productId: string): Menu {
  const { [productId]: _removed, ...products } = menu.products;
  const modifierGroups = Object.fromEntries(
    Object.entries(menu.modifierGroups).map(([groupId, group]) => [
      groupId,
      group.items.some((item) => item.productId === productId)
        ? { ...group, items: group.items.filter((item) => item.productId !== productId) }
        : group,
    ]),
  );
  return { ...menu, categories: outOfOrders(menu.categories, productId, null), products, modifierGroups };
}

/**
 * A copy stands right below its original in a category with an order of its own (Furkan, 05/10/2026), not
 * at the end where a new product comes. In a category by the alphabet, or with an original the order does
 * not name, nothing changes: the copy is where the alphabet puts it.
 */
export function placeBelow(menu: Menu, categoryId: string, productId: string, originalId: string): Menu {
  return {
    ...menu,
    categories: menu.categories.map((category) => {
      const order = category.productOrder;
      if (category.id !== categoryId || !order?.includes(originalId)) return category;
      const rest = order.filter((id) => id !== productId);
      const at = rest.indexOf(originalId) + 1;
      return { ...category, productOrder: [...rest.slice(0, at), productId, ...rest.slice(at)] };
    }),
  };
}

/** Dupliceren (prototype): "(kopie)" after the name, "2" after the code until it is unique. */
export function duplicateDraft(menu: Menu, draft: ProductDraft): ProductDraft {
  let code = `${normalizeCode(draft.code)}2`;
  while (Object.values(menu.products).some((product) => product.code === code)) code += "2";
  return { ...draft, name: `${draft.name.trim()} (kopie)`, code };
}

// choice groups

export type GroupKind = "extra" | "choice" | "instruction";

export function groupKind(group: ModifierGroup): GroupKind {
  return group.kind === "INSTRUCTION" ? "instruction" : group.kind === "CHOICE" ? "choice" : "extra";
}

/** The label on the cards: Verplichte keuze, Instructie, Keuze met gratis or Extra's. */
export function groupLabel(group: ModifierGroup): string {
  if (group.kind === "CHOICE") return "Verplichte keuze";
  if (group.kind === "INSTRUCTION") return "Instructie";
  return group.freeQuantity > 0 ? "Keuze met gratis" : "Extra's";
}

export function productsUsing(menu: Menu, groupId: string): Product[] {
  return Object.values(menu.products).filter((product) => product.modifierGroupIds?.includes(groupId));
}

/** Where a keuzegroep is used, per category: the whole category, or which of its products. */
export interface GroupUse {
  category: string;
  /** Every product of the category uses it. */
  all: boolean;
  /** How many products the category has. */
  of: number;
  /** The products that use it, in the order of the category. */
  products: string[];
}

/**
 * Which categories and products a keuzegroep is linked to (Furkan, 10/10/2026: "ik moet wel zien aan
 * welke categorieën en of producten deze keuzegroepen gekoppeld zijn"), in the order of the menu.
 */
export function groupUse(menu: Menu, groupId: string): GroupUse[] {
  const uses: GroupUse[] = [];
  for (const category of menu.categories) {
    const all = productsIn(menu, category.id);
    const using = all.filter((product) => product.modifierGroupIds?.includes(groupId));
    if (using.length === 0) continue;
    uses.push({
      category: category.name,
      all: using.length === all.length,
      of: all.length,
      products: using.map((product) => product.name),
    });
  }
  return uses;
}

/** The sizes a group prices per size: those of the products that use it (else Small / Medium / Large). */
export function groupSizes(menu: Menu, group: ModifierGroup | null): string[] {
  const labels: string[] = [];
  for (const product of group ? productsUsing(menu, group.id) : []) {
    if (product.variants.length < 2) continue;
    for (const variant of product.variants) if (!labels.includes(variant.label)) labels.push(variant.label);
  }
  if (labels.length > 0) return labels;
  for (const item of group?.items ?? []) {
    for (const label of Object.keys(item.pricesBySize ?? {})) if (!labels.includes(label)) labels.push(label);
  }
  return labels.length > 0 ? labels : DEFAULT_SIZES;
}

export interface OptionDraft {
  /** The product behind the option; undefined for a new option. */
  productId: string | undefined;
  name: string;
  price: string;
  prices: Record<string, string>;
  /** A product of its own category (a pizza in a Splitza half): its name is changed there. */
  fixedName: boolean;
  /** Only for these kinds of order ("Gesneden" only at the table); null = every kind. */
  channels: Channel[] | null;
}

/** The choices for "Bij" of an option: every kind of order, or some of them. */
export const CHANNEL_CHOICES: readonly (readonly Channel[] | null)[] = [
  null,
  ["table"],
  ["takeaway", "delivery"],
  ["takeaway"],
  ["delivery"],
  ["takeaway", "table"],
  ["delivery", "table"],
];

const CHANNEL_ORDER: readonly Channel[] = ["takeaway", "delivery", "table"];

/** The same set of kinds of order, whatever the order ("takeaway,table"); "" = every kind. */
export const channelsKey = (channels: readonly Channel[] | null | undefined): string =>
  channels ? CHANNEL_ORDER.filter((channel) => channels.includes(channel)).join(",") : "";

export interface GroupDraft {
  title: string;
  kind: GroupKind;
  minimum: string;
  maximum: string;
  free: string;
  bySize: boolean;
  options: OptionDraft[];
}

function itemPrice(item: ModifierItem | undefined): number {
  return item?.priceAdjustment?.value ?? 0;
}

export function groupDraft(menu: Menu, group: ModifierGroup | null): GroupDraft {
  if (!group) {
    return {
      title: "",
      kind: "extra",
      minimum: "1",
      maximum: "0",
      free: "0",
      bySize: false,
      options: [{ productId: undefined, name: "", price: "0", prices: {}, fixedName: false, channels: null }],
    };
  }
  const sizes = groupSizes(menu, group);
  return {
    title: group.title,
    kind: groupKind(group),
    minimum: String(Math.max(1, group.minimum)),
    maximum: String(group.maximum),
    free: String(group.freeQuantity),
    bySize: group.items.some((item) => item.pricesBySize !== undefined),
    options: group.items.map((item) => {
      const product = menu.products[item.productId];
      return {
        productId: item.productId,
        name: product?.name ?? item.productId,
        price: decimal(itemPrice(item)),
        prices: Object.fromEntries(sizes.map((size) => [size, decimal(item.pricesBySize?.[size] ?? itemPrice(item))])),
        fixedName: product !== undefined && product.categoryId !== null,
        channels: item.channels && item.channels.length < 3 ? item.channels : null,
      };
    }),
  };
}

export function checkGroup(draft: GroupDraft): string | null {
  if (draft.title.trim() === "") return "Geef de groep een naam";
  if (!draft.options.some((option) => option.name.trim() !== "")) return "Voeg minstens één optie toe";
  return null;
}

const whole = (text: string, minimum: number) => Math.max(minimum, Number.parseInt(text, 10) || 0);
const euros = (text: string) => {
  const value = parseEuros(text);
  return Number.isFinite(value) && value >= 0 ? value : 0;
};

/**
 * Saves a choice group. Every option is a product of the menu (R1 of the menu prompt): an existing
 * product with the same name is reused (one "Americain" for the sauces and the Sauzen category),
 * otherwise a new product without category is made. Where a garnish and a dish share a name (the pizza
 * Scampi), the option is the garnish, in whatever order the menu lists them. Options nobody uses any
 * more are removed.
 */
export function saveGroup(menu: Menu, group: ModifierGroup | null, draft: GroupDraft): { menu: Menu; id: string } {
  const products = { ...menu.products };
  const sizes = groupSizes(menu, group);
  const kind = draft.kind;
  const minimum = kind === "choice" ? whole(draft.minimum, 1) : 0;
  const maximum = kind === "choice" ? Math.max(whole(draft.maximum, 0), minimum) : whole(draft.maximum, 0);
  const items: ModifierItem[] = [];
  for (const option of draft.options) {
    const name = option.name.trim();
    if (name === "") continue;
    let productId = option.productId;
    if (productId !== undefined && products[productId]) {
      const product = products[productId]!;
      if (product.categoryId === null && product.name !== name) products[productId] = { ...product, name };
    } else {
      const named = Object.values(products).filter((product) => product.name.toLowerCase() === name.toLowerCase());
      const same = named.find((product) => product.categoryId === null) ?? named[0];
      if (same) productId = same.id;
      else {
        productId = newId("prod_", name, (taken) => Object.hasOwn(products, taken));
        products[productId] = {
          id: productId,
          name,
          categoryId: null,
          variants: [{ label: DEFAULT_VARIANT, price: 0 }],
        };
      }
    }
    if (items.some((item) => item.productId === productId)) continue;
    const before = group?.items.find((item) => item.productId === productId);
    const {
      pricesBySize: _sizes,
      priceAdjustment: _price,
      channels: _channels,
      ...kept
    } = before ?? ({} as Partial<ModifierItem>);
    const item: ModifierItem = { ...kept, productId: productId };
    // every kind of order: nothing to store
    const only = option.channels;
    if (only !== null && only.length > 0 && only.length < CHANNEL_ORDER.length) {
      item.channels = CHANNEL_ORDER.filter((channel) => only.includes(channel));
    }
    if (kind !== "instruction") {
      if (draft.bySize) {
        item.pricesBySize = Object.fromEntries(sizes.map((size) => [size, euros(option.prices[size] ?? option.price)]));
      } else {
        item.priceAdjustment = { method: "SET_AMOUNT", value: euros(option.price) };
      }
    }
    items.push(item);
  }

  const fields = {
    title: draft.title.trim(),
    kind: kind === "instruction" ? "INSTRUCTION" : kind === "choice" ? "CHOICE" : "EXTRA",
    selectionMode: kind === "choice" && maximum === 1 ? "SINGLE" : "MULTIPLE",
    minimum,
    maximum,
    freeQuantity: kind === "extra" ? whole(draft.free, 0) : 0,
    items,
  } satisfies Omit<ModifierGroup, "id">;
  const id = group?.id ?? newId("mg_", fields.title, (taken) => Object.hasOwn(menu.modifierGroups, taken));
  const modifierGroups = { ...menu.modifierGroups, [id]: { ...(group ?? {}), id, ...fields } };
  return { menu: withoutUnusedOptions({ ...menu, products, modifierGroups }), id };
}

/** Deletes a group and unlinks it from every product (prototype: "Ze wordt losgekoppeld van N producten"). */
export function deleteGroup(menu: Menu, groupId: string): Menu {
  const { [groupId]: _removed, ...modifierGroups } = menu.modifierGroups;
  const products = Object.fromEntries(
    Object.entries(menu.products).map(([productId, product]) => {
      if (!product.modifierGroupIds?.includes(groupId)) return [productId, product];
      const { [groupId]: _free, ...freeQuantities } = product.freeQuantities ?? {};
      const { freeQuantities: _old, ...rest } = product;
      return [
        productId,
        {
          ...rest,
          modifierGroupIds: product.modifierGroupIds.filter((id) => id !== groupId),
          ...(Object.keys(freeQuantities).length > 0 ? { freeQuantities } : {}),
        },
      ];
    }),
  );
  return withoutUnusedOptions({ ...menu, products, modifierGroups });
}

/** Options without category that no group offers any more are of no use: they leave the menu. */
function withoutUnusedOptions(menu: Menu): Menu {
  const used = new Set(
    Object.values(menu.modifierGroups).flatMap((group) => group.items.map((item) => item.productId)),
  );
  const products = Object.fromEntries(
    Object.entries(menu.products).filter(([id, product]) => product.categoryId !== null || used.has(id)),
  );
  return { ...menu, products };
}

// the order of the products (Furkan, 03/10/2026)

/** Products of a category in the order of the till: the shop's own order, else the alphabet. */
export function productsOf(menu: Menu, categoryId: string): Product[] {
  return productsIn(menu, categoryId);
}

/** Whether the shop put the products of this category in an order of its own. */
export function hasOwnOrder(menu: Menu, categoryId: string): boolean {
  return menu.categories.find((category) => category.id === categoryId)?.productOrder !== undefined;
}

function withProductOrder(menu: Menu, categoryId: string, order: string[] | null): Menu {
  return {
    ...menu,
    categories: menu.categories.map((category) => {
      if (category.id !== categoryId) return category;
      const { productOrder: _order, ...rest } = category;
      return order === null ? rest : { ...rest, productOrder: order };
    }),
  };
}

/** "Eigen volgorde": the products stay as they stand now; a new product comes last. */
export function ownOrder(menu: Menu, categoryId: string): Menu {
  return withProductOrder(
    menu,
    categoryId,
    productsIn(menu, categoryId).map((product) => product.id),
  );
}

/** "Alfabetisch": the specialties first, then the alphabet again, also for every new product. */
export function alphabeticalOrder(menu: Menu, categoryId: string): Menu {
  return withProductOrder(menu, categoryId, null);
}

/**
 * Puts a product at another place among the products of its category (0 is the first place): from then
 * on the category has an order of its own.
 */
export function moveProduct(menu: Menu, productId: string, to: number): Menu {
  const categoryId = menu.products[productId]?.categoryId;
  if (categoryId == null) return menu;
  const ids = productsIn(menu, categoryId).map((product) => product.id);
  const from = ids.indexOf(productId);
  const place = Math.max(0, Math.min(ids.length - 1, to));
  if (from === place) return menu;
  return withProductOrder(menu, categoryId, moved(ids, from, place));
}
